import type { DragEvent, ReactNode } from 'react';
import { api } from '../api.ts';
import {
  BLATTLINIE, bandwort, istBild, kacheltitel, symbolfarbe,
} from './dateiart.ts';

/**
 * EINE DATEIKACHEL, UND ZWAR GENAU EINE IM GANZEN HAUS.
 *
 * Sie stand zweimal da: im Dateireiter mit gezeichnetem Blatt, in der
 * Auftragsakte (Umgebungsbilder) ohne. Die zweite konnte damit weniger - eine
 * PDF stand dort als kaputtes Bildzeichen -, und der Kommentar darüber sagte,
 * dass zwei Fassungen auseinanderlaufen. **Das war eine Ankündigung und keine
 * Begründung.** Ab jetzt gibt es eine, und was die Bildschirme unterscheidet,
 * steht als benannte Eigenschaft daneben.
 *
 * DIE REGELN LIEGEN OHNE JSX IN `dateiart.ts`. Diese Datei zeichnet nur.
 */

/**
 * Das gezeichnete Blatt für alles, was kein Vorschaubild hat.
 *
 * Es trägt eine FESTE graue Linie und erbt keine Linkfarbe: ein Sinnbild ist
 * kein Zustand (siehe `BLATTLINIE`).
 */
export function Dateisymbol({ name }: { name: string }) {
  const farbe = symbolfarbe(name);
  const wort = bandwort(name);
  const gross = wort.length >= 4 ? 8.5 : wort.length === 3 ? 10.5 : 11.5;

  return (
    <svg className="dateisymbol" viewBox="0 0 48 60" role="img"
         aria-label={wort ? `${wort}-Datei` : 'Datei'}>
      {/* Das Blatt. */}
      <path d="M6 1.5h20.5L44 19v37.5a2.5 2.5 0 0 1-2.5 2.5h-35.5A2.5 2.5 0
               0 1 3.5 56.5V4A2.5 2.5 0 0 1 6 1.5z"
            fill="var(--grund)" stroke={BLATTLINIE} strokeWidth="1.6"
            strokeLinejoin="round" />
      {/* Die umgeknickte Ecke. */}
      <path d="M26.5 1.5V16a3 3 0 0 0 3 3H44"
            fill="none" stroke={BLATTLINIE} strokeWidth="1.6"
            strokeLinejoin="round" />
      {/* Das Band mit der Art. */}
      <rect x="1" y="30" width="37" height="16" rx="2.5" fill={farbe} />
      {wort && (
        <text x="19.5" y="41.6" textAnchor="middle" fill="#FFFFFF"
              fontSize={gross} fontWeight="700"
              fontFamily="system-ui, sans-serif" letterSpacing=".3">
          {wort}
        </text>
      )}
      {/* Eine angedeutete Textzeile, damit das Blatt nicht leer wirkt. */}
      <path d="M11 51h26" stroke={BLATTLINIE} strokeWidth="1.6"
            strokeLinecap="round" opacity=".55" />
    </svg>
  );
}

/** Was eine Kachel über ihre Datei wissen muss. */
export type Kacheldatei = {
  /** Die Kennung des DATEIBEZUGS - das ist die Adresse zum Ausliefern. */
  id: string;
  dateiname: string;
  mime?: string | null;
  groesse?: number | null;
  /**
   * WO DIE DATEI LIEGT, WENN SIE NICHT AN EINEM AUFTRAG HÄNGT.
   *
   * Der Regelfall ist eine abgelegte Datei, und die hat einen `datei_bezug`;
   * daraus baut die Kachel ihre Adresse selbst. Ein Anhang, der noch im
   * POSTFACH liegt, hat keinen: er kommt über `mailfachAnhangWeg` und wird
   * erst abgelegt, wenn die Mail an einen Auftrag geht. **Zwei Kacheln zu
   * bauen, weil die Adresse aus zwei Töpfen kommt, wäre die zweite Fassung,
   * die beim ersten Nachtrag weniger kann** - dieselbe Sache, die es hier
   * schon einmal gab.
   */
  adresse?: string;
};

/**
 * Das Zeichen zum Herunterladen: ein Pfeil auf eine Grundlinie.
 *
 * Es ist gezeichnet und nicht geschrieben, weil ein Buchstabe an dieser
 * Stelle wie eine Abkürzung aussähe. Es erbt die Farbe seines Knopfes
 * (`currentColor`), damit es beim Überfahren mitgeht.
 */
function Holzeichen() {
  return (
    <svg viewBox="0 0 16 16" role="img" aria-hidden="true" focusable="false">
      <path d="M8 2.5v7.5M4.75 7.25 8 10.5l3.25-3.25M3 13h10"
            fill="none" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Eine Kachel: Vorschaubild oder gezeichnetes Blatt, darunter der Name.
 *
 * DER NAME GEHÖRT AN DAS, WAS ER BENENNT. Auf dem Telefon gibt es keinen
 * Tooltip, und `IMG_20240513_1015` und `IMG_20240513_1042` unterscheiden sich
 * in genau den Zeichen, die ein „..." frisst.
 *
 * ZWEI WEGE, UND DER AUFRUFER ENTSCHEIDET. Mit `oeffne` ist die Kachel ein
 * KNOPF und zeigt die Datei in einem Fenster (Punkt 7: „Anhänge in einem
 * Fenster statt neuem Tab"); ohne ihn ist sie ein Link in einen neuen Tab.
 * **Das ist eine Eigenschaft und keine zweite Fassung** - beide zeichnen
 * dasselbe.
 */
export function Dateikachel({
  datei, oeffne, gewaehlt, ecke, zieh, children,
}: {
  datei: Kacheldatei;
  oeffne?: (d: Kacheldatei) => void;
  /** Eine grüne Kante, wo eine Wahl darauf liegt. */
  gewaehlt?: boolean;
  /**
   * DIE KACHEL LÄSST SICH ANFASSEN UND IN EINEN ANDEREN ORDNER ZIEHEN.
   *
   * ORIGINALTEXT MARVIN (09.10.2026)
   *   „es kann sein dass die partner die bilder falsch sortieren, ich will
   *    die bilder im innendienst verschieben können ohne dass ich sie erst
   *    herunterladen muss um sie dann im richtigen ordner wieder hochladen
   *    zu müssen. am besten per drag&drop"
   *
   * Der Aufrufer sagt, was beim Anfassen und beim Loslassen geschieht; die
   * Kachel trägt nur `draggable` und meldet beides. Ohne `zieh` ist sie so
   * unbeweglich wie bisher - der Monteur am Telefon zieht nichts.
   */
  zieh?: { beginn: (e: DragEvent) => void; ende: () => void; laeuft?: boolean };
  /**
   * WAS IN DIE ECKE OBEN RECHTS GEHÖRT, NEBEN DAS ZEICHEN ZUM HERUNTERLADEN.
   *
   * Der Aufrufer gibt hier sein X hinein (wer hochladen darf, darf abhängen);
   * die Kachel stellt es in dieselbe Leiste wie ihr eigenes Zeichen. Stünde
   * es wie bisher in `children`, trüge es seine eigene absolute Lage, und die
   * beiden Knöpfe lägen übereinander.
   */
  ecke?: ReactNode;
  /** Was zusätzlich auf oder unter der Kachel steht (Merker, Häkchen). */
  children?: ReactNode;
}) {
  const titel = kacheltitel(datei.dateiname, datei.groesse);
  /* DIE ADRESSE STEHT EINMAL DA. Wo der Aufrufer eine mitbringt, gilt seine;
     sonst hängt die Datei an einem Auftrag, und dann kennt die Kachel den
     Weg selbst. */
  const adresse = datei.adresse
    ?? (datei.id ? api.dateiAdresse(datei.id, datei.dateiname) : '');
  /* EIN VORSCHAUBILD, DAS NICHT KOMMT, IST SCHLIMMER ALS EIN GEZEICHNETES
     BLATT. Ohne Adresse gibt es keinen Weg zur Datei - ein `<img>` darauf
     wäre ein kaputtes Bildzeichen, und ein Link ins Leere sieht aus wie
     einer, der klemmt. Dann steht das Blatt da, und die Kachel ist kein
     Weg, sondern eine Auskunft. */
  const inhalt = adresse && istBild(datei.mime, datei.dateiname)
    ? <img src={adresse} alt={datei.dateiname} loading="lazy" decoding="async" />
    : <Dateisymbol name={datei.dateiname} />;

  if (!adresse) {
    return (
      <div className={'dateikachel' + (gewaehlt ? ' gewaehlt' : '')}>
        {/* OHNE ADRESSE GIBT ES NICHTS ZUM HERUNTERLADEN, und deshalb steht
            hier kein Zeichen dafür. **Ein Knopf, der ins Leere führt, ist
            schlimmer als keiner:** wer ihn drückt, bekommt eine leere Datei
            und hält die Ablage für kaputt. Das X des Aufrufers bleibt - eine
            Datei, zu der kein Weg führt, will man erst recht abhängen. */}
        {ecke && <div className="kachelecke">{ecke}</div>}
        <span className="kachelbild" title={titel}>{inhalt}</span>
        <span className="kachelname" title={titel}>{datei.dateiname}</span>
        {children}
      </div>
    );
  }

  return (
    <div className={'dateikachel' + (gewaehlt ? ' gewaehlt' : '')
                    + (zieh ? ' ziehbar' : '') + (zieh?.laeuft ? ' zieht' : '')}
         draggable={zieh ? true : undefined}
         onDragStart={zieh ? zieh.beginn : undefined}
         onDragEnd={zieh ? zieh.ende : undefined}
         title={zieh ? 'Zum Verschieben in einen anderen Ordner ziehen' : undefined}>
      {/* DAS ZEICHEN ZUM HERUNTERLADEN STEHT NEBEN DEM LÖSCHZEICHEN.
          =================================================================
          Es geht auf DIESELBE Adresse wie die Kachel selbst - der Weg, der
          den Dateinamen im PFAD trägt (27.08.2026: wer eine PDF im
          eingebauten Betrachter speichert, bekommt sonst die Kennung des
          Dateibezugs als Vorschlag, und nach zwanzig Nachweisen liegen
          zwanzig UUID im Ordner).

          `download` sagt dem Browser, dass er sie ABLEGEN und nicht ÖFFNEN
          soll - beim Bild und bei der PDF ist das der ganze Unterschied zum
          Klick auf die Kachel daneben. Es trägt den Dateinamen ein zweites
          Mal: bei gleicher Herkunft gewinnt er gegen alles, was der Server
          im Kopf mitschickt. */}
      <div className="kachelecke">
        <a className="kachelholen" href={adresse} download={datei.dateiname}
           title={`${datei.dateiname} herunterladen`}
           aria-label={`${datei.dateiname} herunterladen`}
           onClick={(e) => e.stopPropagation()}>
          <Holzeichen />
        </a>
        {ecke}
      </div>

      {oeffne ? (
        <button type="button" className="kachelbild" title={titel}
                onClick={() => oeffne(datei)}>
          {inhalt}
        </button>
      ) : (
        <a href={adresse} target="_blank" rel="noreferrer"
           className="kachelbild" title={titel}>
          {inhalt}
        </a>
      )}

      {oeffne ? (
        <button type="button" className="kachelname" title={titel}
                onClick={() => oeffne(datei)}>
          {datei.dateiname}
        </button>
      ) : (
        <a href={adresse} target="_blank" rel="noreferrer"
           className="kachelname" title={titel}>
          {datei.dateiname}
        </a>
      )}

      {children}
    </div>
  );
}
